import { useState, useEffect, useCallback, useRef } from "react"

/**
 * @typedef {Object} UIState
 * @property {boolean} leftSidebarOpen - Whether the left sidebar is open
 * @property {boolean} rightSidebarOpen - Whether the right sidebar is open
 * @property {boolean} elementsDrawerVisible - Whether the elements drawer is visible (mobile)
 * @property {boolean} slidesDrawerVisible - Whether the slides drawer is visible (mobile)
 * @property {boolean} isFullScreen - Whether the application is in fullscreen mode
 * @property {React.RefObject<HTMLDivElement>} containerRef - Ref for the main container element
 * @property {() => void} toggleFullScreen - Function to toggle fullscreen mode
 * @property {() => void} toggleLeftSidebar - Function to toggle left sidebar/elements drawer
 * @property {() => void} toggleRightSidebar - Function to toggle right sidebar/slides drawer
 * @property {() => void} toggleElementsDrawer - Function to toggle elements drawer
 * @property {() => void} toggleSlidesDrawer - Function to toggle slides drawer
 */

/**
 * Extended HTMLDivElement interface for fullscreen API compatibility
 */
interface ExtendedHTMLDivElement extends HTMLDivElement {
  webkitRequestFullscreen?: () => Promise<void>
  msRequestFullscreen?: () => Promise<void>
}

/**
 * Extended Document interface for fullscreen API compatibility
 */
interface ExtendedDocument extends Document {
  webkitExitFullscreen?: () => Promise<void>
  msExitFullscreen?: () => Promise<void>
}

/**
 * Return type for the useUIState hook
 */
export interface UIState {
  leftSidebarOpen: boolean
  rightSidebarOpen: boolean
  elementsDrawerVisible: boolean
  slidesDrawerVisible: boolean
  isFullScreen: boolean
  containerRef: React.RefObject<HTMLDivElement | null>
  toggleFullScreen: () => void
  toggleLeftSidebar: () => void
  toggleRightSidebar: () => void
  toggleElementsDrawer: () => void
  toggleSlidesDrawer: () => void
}

/**
 * Custom hook for managing UI state including sidebars, drawers, and fullscreen functionality.
 *
 * Handles responsive behavior by automatically adjusting sidebar visibility based on screen size.
 * Provides fullscreen detection and control, with cross-browser compatibility.
 * Manages both desktop sidebars and mobile drawers with appropriate toggle functions.
 *
 * @function useUIState
 * @param {boolean} isSmallScreen - Whether the screen is considered small (< 1400px)
 * @returns {UIState} Object containing UI state and control functions
 *
 */
export const useUIState = (isSmallScreen: boolean): UIState => {
  /** Left sidebar visibility state */
  const [leftSidebarOpen, setLeftSidebarOpen] = useState<boolean>(true)

  /** Right sidebar visibility state */
  const [rightSidebarOpen, setRightSidebarOpen] = useState<boolean>(true)

  /** Elements drawer visibility state (mobile) */
  const [elementsDrawerVisible, setElementsDrawerVisible] =
    useState<boolean>(false)

  /** Slides drawer visibility state (mobile) */
  const [slidesDrawerVisible, setSlidesDrawerVisible] = useState<boolean>(false)

  /** Fullscreen mode state */
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false)

  /** Reference to the main container element */
  const containerRef = useRef<HTMLDivElement>(null)

  /**
   * Auto-adjust sidebars based on screen size changes
   * On small screens: close sidebars, use drawers instead
   * On large screens: open sidebars, close drawers
   */
  useEffect(() => {
    if (isSmallScreen) {
      setLeftSidebarOpen(false)
      setRightSidebarOpen(false)
    } else {
      setLeftSidebarOpen(true)
      setRightSidebarOpen(true)
    }
  }, [isSmallScreen])

  /**
   * Fullscreen detection effect with cross-browser support
   * Listens to various fullscreen change events and updates state accordingly
   */
  useEffect(() => {
    /**
     * Handles fullscreen change events across different browsers
     */
    const handleFullscreenChange = (): void =>
      setIsFullScreen(!!document.fullscreenElement)

    // Add event listeners for different browsers
    document.addEventListener("fullscreenchange", handleFullscreenChange)
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange)
    document.addEventListener("mozfullscreenchange", handleFullscreenChange)
    document.addEventListener("MSFullscreenChange", handleFullscreenChange)

    // Cleanup function to remove all event listeners
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange)
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      )
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      )
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange)
    }
  }, [])

  /**
   * Toggles fullscreen mode with cross-browser support
   */
  const toggleFullScreen = useCallback((): void => {
    if (!isFullScreen) {
      // Enter fullscreen with browser fallbacks
      const element = containerRef.current as ExtendedHTMLDivElement | null
      if (element?.requestFullscreen) {
        element.requestFullscreen()
      } else if (element?.webkitRequestFullscreen) {
        element.webkitRequestFullscreen()
      } else if (element?.msRequestFullscreen) {
        element.msRequestFullscreen()
      }
    } else {
      // Exit fullscreen with browser fallbacks
      const doc = document as ExtendedDocument
      if (doc.exitFullscreen) {
        doc.exitFullscreen()
      } else if (doc.webkitExitFullscreen) {
        doc.webkitExitFullscreen()
      } else if (doc.msExitFullscreen) {
        doc.msExitFullscreen()
      }
    }
  }, [isFullScreen])

  /**
   * Toggles left sidebar on desktop or elements drawer on mobile
   */
  const toggleLeftSidebar = useCallback((): void => {
    if (isSmallScreen) {
      setElementsDrawerVisible(!elementsDrawerVisible)
    } else {
      setLeftSidebarOpen(!leftSidebarOpen)
    }
  }, [isSmallScreen, leftSidebarOpen, elementsDrawerVisible])

  /**
   * Toggles right sidebar on desktop or slides drawer on mobile
   */
  const toggleRightSidebar = useCallback((): void => {
    if (isSmallScreen) {
      setSlidesDrawerVisible(!slidesDrawerVisible)
    } else {
      setRightSidebarOpen(!rightSidebarOpen)
    }
  }, [isSmallScreen, rightSidebarOpen, slidesDrawerVisible])

  /**
   * Toggles elements drawer visibility (mobile only)
   */
  const toggleElementsDrawer = useCallback((): void => {
    setElementsDrawerVisible(!elementsDrawerVisible)
  }, [elementsDrawerVisible])

  /**
   * Toggles slides drawer visibility (mobile only)
   */
  const toggleSlidesDrawer = useCallback((): void => {
    setSlidesDrawerVisible(!slidesDrawerVisible)
  }, [slidesDrawerVisible])

  return {
    leftSidebarOpen,
    rightSidebarOpen,
    elementsDrawerVisible,
    slidesDrawerVisible,
    isFullScreen,
    containerRef,
    toggleFullScreen,
    toggleLeftSidebar,
    toggleRightSidebar,
    toggleElementsDrawer,
    toggleSlidesDrawer,
  }
}
